'use client';

import { useMemo, useState } from 'react';
import {
  DEFAULT_DEOBFUSCATE_OPTIONS,
  DEOBFUSCATE_SAMPLE,
  deobfuscateJs,
  type DeobfuscateOptions,
} from '@/lib/browser-jsobfuscate-tools';
import { runCodeTool } from '@/lib/browser-code-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

function beautify(code: string): string {
  return runCodeTool('javascript-beautifier', code);
}

export default function BrowserJavascriptDeobfuscator() {
  const [input, setInput] = useState<string>(DEOBFUSCATE_SAMPLE);
  const [options, setOptions] = useState<DeobfuscateOptions>(DEFAULT_DEOBFUSCATE_OPTIONS);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const { output, error } = useMemo(() => {
    try {
      return { output: deobfuscateJs(input, options, beautify), error: null as string | null };
    } catch {
      return {
        output: '',
        error: 'The code could not be processed. Check for unusual syntax and try again.',
      };
    }
  }, [input, options]);

  function update<K extends keyof DeobfuscateOptions>(key: K, value: DeobfuscateOptions[K]) {
    setOptions((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">JavaScript</span>
        <h2 className="tool-live-title">JavaScript deobfuscator</h2>
        <p className="tool-live-subtitle">
          Decode \xNN and \uNNNN escapes, unpack String.fromCharCode sequences, and re-indent
          minified JavaScript so it is readable again. It cannot restore original variable names
          lost to minification.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Obfuscated JavaScript</span>
              <textarea
                onChange={(e) => setInput(e.target.value)}
                rows={14}
                spellCheck={false}
                value={input}
              />
            </label>
            <label
              className="tool-live-field"
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <input
                checked={options.decodeEscapes}
                onChange={(e) => update('decodeEscapes', e.target.checked)}
                type="checkbox"
              />
              <span>Decode \x and \u escapes</span>
            </label>
            <label
              className="tool-live-field"
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <input
                checked={options.decodeCharCodes}
                onChange={(e) => update('decodeCharCodes', e.target.checked)}
                type="checkbox"
              />
              <span>Decode String.fromCharCode</span>
            </label>
            <label
              className="tool-live-field"
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <input
                checked={options.beautify}
                onChange={(e) => update('beautify', e.target.checked)}
                type="checkbox"
              />
              <span>Pretty-print and indent</span>
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Readable JavaScript</span>
            <textarea readOnly rows={18} spellCheck={false} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Processed locally in your browser. Minified names cannot be recovered.
          </p>
        </section>
      </div>
    </div>
  );
}
